/* eslint-disable better-mutation/no-mutation */
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type {
    ItemDraftResponse,
    GetCategoryRestrictionApiResponse,
} from 'tradera/state/services/types/webapi-selling-generated';

type SyiState = {
    creationStart: number | null;
    allErrorsVisible: boolean;
    isEditOfPublishedItem: boolean;
    publishPending: boolean;
    publishFailed: boolean;
    publishFailedReasonDisplayText: string | null;
    publishedItemPendingVip: { itemId: string } | null;
    publishedItem: ItemDraftResponse | null;
    openModal: string | null;
    openModalData: null;
    autoSaveEnabled: boolean;
    draftCanBeResumed: boolean;
    itemUpdating: boolean;
    itemUpdated: boolean;
    isDraftSaved: boolean;
    publishDelayed: boolean;
    categoryRestriction: GetCategoryRestrictionApiResponse | null;
    fadeUi: boolean;
};

const initialState: SyiState = {
    creationStart: null,
    allErrorsVisible: false,
    isEditOfPublishedItem: false,
    publishPending: false,
    publishFailed: false,
    publishFailedReasonDisplayText: null,
    publishedItemPendingVip: null,
    publishedItem: null,
    openModal: null,
    openModalData: null,
    autoSaveEnabled: false,
    draftCanBeResumed: false,
    itemUpdating: false,
    itemUpdated: false,
    isDraftSaved: false,
    publishDelayed: false,
    categoryRestriction: null,
    fadeUi: false,
};

const syiSlice = createSlice({
    name: 'syi',
    initialState,
    reducers: {
        resetSyi: () => initialState,
        /**
         *
         * @param action Epoch
         */
        setCreationStart: (state, action: PayloadAction<number>) => {
            state.creationStart = action.payload;
        },
        showAllErrors: (state) => {
            state.allErrorsVisible = true;
        },
        hideAllErrors: (state) => {
            state.allErrorsVisible = false;
        },
        setIsEditOfPublishedItem: (state, action: PayloadAction<boolean>) => {
            state.isEditOfPublishedItem = action.payload;
        },
        setPublishedItemPendingVip: (
            state,
            action: PayloadAction<{ itemId: string }>,
        ) => {
            state.publishedItemPendingVip = action.payload;
            state.publishedItem = null;
            state.publishFailed = false;
            state.publishFailedReasonDisplayText = null;
            state.publishPending = false;
            state.publishDelayed = false;
        },
        setPublishedItemDelayed: (
            state,
            action: PayloadAction<{ itemId: string }>,
        ) => {
            state.publishedItemPendingVip = action.payload;
            state.publishedItem = null;
            state.publishFailed = false;
            state.publishFailedReasonDisplayText = null;
            state.publishPending = false;
            state.publishDelayed = true;
        },
        setPublishedItem: (state, action: PayloadAction<ItemDraftResponse>) => {
            state.publishedItem = action.payload;
            state.publishedItemPendingVip = null;
            state.publishFailed = false;
            state.publishFailedReasonDisplayText = null;
            state.publishPending = false;
            state.publishDelayed = false;
        },
        setPublishFailed: (state, action: PayloadAction<string>) => {
            state.publishedItem = null;
            state.publishedItemPendingVip = null;
            state.publishFailed = true;
            state.publishFailedReasonDisplayText = action.payload;
            state.publishPending = false;
            state.publishDelayed = false;
        },
        setPublishPending: (state) => {
            state.publishedItem = null;
            state.publishedItemPendingVip = null;
            state.publishFailed = false;
            state.publishFailedReasonDisplayText = null;
            state.publishPending = true;
            state.publishDelayed = false;
        },
        setCategoryRestriction: (
            state,
            action: PayloadAction<GetCategoryRestrictionApiResponse>,
        ) => {
            state.categoryRestriction = action.payload;
        },
        toggleModal: (state, action: PayloadAction<string>) => {
            state.openModal =
                state.openModal === action.payload ? null : action.payload;
        },
        enableAutoSave: (state) => {
            state.autoSaveEnabled = true;
        },
        disableAutoSave: (state) => {
            state.autoSaveEnabled = false;
        },
        setDraftCanBeResumed: (state, action: PayloadAction<boolean>) => {
            state.draftCanBeResumed = action.payload;
        },
        setItemUpdating: (state, action: PayloadAction<boolean>) => {
            state.itemUpdating = action.payload;
        },
        setItemUpdated: (state, action: PayloadAction<boolean>) => {
            state.itemUpdated = action.payload;
        },
        setIsDraftSaved: (state, action: PayloadAction<boolean>) => {
            state.isDraftSaved = action.payload;
        },
        setFadeUi: (state, action: PayloadAction<boolean>) => {
            state.fadeUi = action.payload;
        },
    },
});

export const {
    resetSyi,
    setCreationStart,
    showAllErrors,
    hideAllErrors,
    setIsEditOfPublishedItem,
    setPublishedItemPendingVip,
    setPublishedItemDelayed,
    setPublishedItem,
    setPublishFailed,
    setPublishPending,
    setCategoryRestriction,
    toggleModal,
    enableAutoSave,
    disableAutoSave,
    setDraftCanBeResumed,
    setItemUpdating,
    setItemUpdated,
    setIsDraftSaved,
    setFadeUi,
} = syiSlice.actions;

export default syiSlice.reducer;
